import type { GridPrefColumn } from "./apply-grid-prefs.ts";
import type {
	GridIdentifierKey,
	GridIdentifierName,
} from "./grid-identifier-enums.ts";
import {
	gridColumnVisibilityEnums,
	GridIdentifiers,
} from "./grid-identifier-enums.ts";

/**
 * One grid's preferences. Mirrors the per-grid object the API returns inside
 * its keyed-by-id response.
 */
export type GridPrefsDocument = {
	gridId: GridIdentifierName;
	columns: GridPrefColumn[];
};

/**
 * Top-level grid prefs response shape. The API returns one entry per grid the
 * user has prefs for, keyed by the numeric `GridIdentifiers` value (as a
 * string). Use `GridIdentifiers["<grid name>"]` to look up an entry.
 */
export type GridPrefsResponse = Partial<
	Record<GridIdentifierKey, GridPrefsDocument>
>;

const SHIPMENT_BOARD: GridIdentifierName = "Shipment Board";

/**
 * Default Shipment Board grid preferences for internal users.
 */
export const defaultShipmentBoardGridPrefsInternal: GridPrefsResponse = {
	[GridIdentifiers[SHIPMENT_BOARD]]: {
		gridId: SHIPMENT_BOARD,
		columns: [
			{
				columnId: "tracking",
				isVisible: true,
				ordinalPosition: 0,
				widthSize: 40,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "rateType",
				isVisible: true,
				ordinalPosition: 1,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "poNumber",
				isVisible: true,
				ordinalPosition: 2,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "formattedStatus",
				isVisible: true,
				ordinalPosition: 3,
				widthSize: 110,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "customerName",
				isVisible: true,
				ordinalPosition: 4,
				widthSize: 225,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "formattedPickupAddress",
				isVisible: true,
				ordinalPosition: 5,
				widthSize: 175,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "formattedDeliveryAddress",
				isVisible: true,
				ordinalPosition: 6,
				widthSize: 175,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "formattedLastLocation",
				isVisible: true,
				ordinalPosition: 7,
				widthSize: 175,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "carrierName",
				isVisible: true,
				ordinalPosition: 8,
				widthSize: 125,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "proNumber",
				isVisible: true,
				ordinalPosition: 9,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "loadDateValue",
				isVisible: true,
				ordinalPosition: 10,
				widthSize: 125,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "deliveryDateValue",
				isVisible: true,
				ordinalPosition: 11,
				widthSize: 125,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "ltlArName",
				isVisible: true,
				ordinalPosition: 12,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "customerPO",
				isVisible: false,
				ordinalPosition: 13,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "pickupPOList",
				isVisible: false,
				ordinalPosition: 14,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "deliveryPOList",
				isVisible: false,
				ordinalPosition: 15,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "soNumber",
				isVisible: false,
				ordinalPosition: 16,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "quoteId",
				isVisible: false,
				ordinalPosition: 17,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "custRefNum",
				isVisible: false,
				ordinalPosition: 18,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "tenderedCustomerName",
				isVisible: false,
				ordinalPosition: 19,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "ediStatus",
				isVisible: false,
				ordinalPosition: 20,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "primaryBrokerName",
				isVisible: false,
				ordinalPosition: 21,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "omassStatus",
				isVisible: false,
				ordinalPosition: 22,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "ftlGsm",
				isVisible: false,
				ordinalPosition: 23,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "serviceLevel",
				isVisible: false,
				ordinalPosition: 24,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[1],
			},
		],
	},
};

/**
 * Default Shipment Board grid preferences for external users.
 * Default visible: Docs (icon) | TQL PO # | Customer PO | Mode | Status |
 *                  Pickup Date | Pickup Location | Delivery Date | Delivery Location
 * Column chooser order: TQL PO # | Customer PO | Mode | Status | Pickup Date |
 *                       Pickup Location | Delivery Date | Delivery Location |
 *                       Customer Name | Pickup Number | Delivery Number |
 *                       TQL Quote ID | LTL Carrier Name | PRO Number |
 *                       SO Number | Customer Reference
 */
export const defaultShipmentBoardGridPrefsExternal: GridPrefsResponse = {
	[GridIdentifiers[SHIPMENT_BOARD]]: {
		gridId: SHIPMENT_BOARD,
		columns: [
			{
				columnId: "tracking",
				isVisible: true,
				ordinalPosition: 0,
				widthSize: 40,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "poNumber",
				isVisible: true,
				ordinalPosition: 1,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "customerPO",
				isVisible: true,
				ordinalPosition: 2,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[2],
			},
			{
				columnId: "rateType",
				isVisible: true,
				ordinalPosition: 3,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "formattedStatus",
				isVisible: true,
				ordinalPosition: 4,
				widthSize: 110,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "loadDateValue",
				isVisible: true,
				ordinalPosition: 5,
				widthSize: 125,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "formattedPickupAddress",
				isVisible: true,
				ordinalPosition: 6,
				widthSize: 175,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "deliveryDateValue",
				isVisible: true,
				ordinalPosition: 7,
				widthSize: 125,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "formattedDeliveryAddress",
				isVisible: true,
				ordinalPosition: 8,
				widthSize: 175,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "formattedLastLocation",
				isVisible: true,
				ordinalPosition: 9,
				widthSize: 175,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "customerName",
				isVisible: false,
				ordinalPosition: 10,
				widthSize: 225,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "pickupPOList",
				isVisible: false,
				ordinalPosition: 11,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "deliveryPOList",
				isVisible: false,
				ordinalPosition: 12,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "quoteId",
				isVisible: false,
				ordinalPosition: 13,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "ltlCarrierName",
				isVisible: false,
				ordinalPosition: 14,
				widthSize: 125,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "proNumber",
				isVisible: false,
				ordinalPosition: 15,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "soNumber",
				isVisible: false,
				ordinalPosition: 16,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "custRefNum",
				isVisible: false,
				ordinalPosition: 17,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[3],
			},
			{
				columnId: "ltlArName",
				isVisible: false,
				ordinalPosition: 18,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "tenderedCustomerName",
				isVisible: false,
				ordinalPosition: 19,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "ediStatus",
				isVisible: false,
				ordinalPosition: 20,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "primaryBrokerName",
				isVisible: false,
				ordinalPosition: 21,
				widthSize: 150,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "omassStatus",
				isVisible: false,
				ordinalPosition: 22,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "ftlGsm",
				isVisible: false,
				ordinalPosition: 23,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[1],
			},
			{
				columnId: "serviceLevel",
				isVisible: false,
				ordinalPosition: 24,
				widthSize: 100,
				visibility: gridColumnVisibilityEnums[1],
			},
		],
	},
};

/**
 * Returns the appropriate Shipment Board default preferences based on deployment type.
 */
export const getDefaultShipmentBoardGridPrefs = (
	isInternal: boolean,
): GridPrefsResponse =>
	isInternal
		? defaultShipmentBoardGridPrefsInternal
		: defaultShipmentBoardGridPrefsExternal;

/**
 * @deprecated Use `getDefaultShipmentBoardGridPrefs(isInternal)` instead.
 * Kept for backward compatibility — returns internal defaults.
 */
export const defaultShipmentBoardGridPrefs =
	defaultShipmentBoardGridPrefsInternal;

const QUOTE_BOARD: GridIdentifierName = "Quote Board";

/**
 * Default individual Quote Board grid preferences. The trailing action column
 * is intentionally omitted because it is pinned to the end of the grid
 * outside the preference system.
 */
const quoteBoardDefaultColumns: GridPrefColumn[] = [
	{
		columnId: "quoteId",
		isVisible: true,
		ordinalPosition: 0,
		widthSize: 125,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "source",
		isVisible: false,
		ordinalPosition: 1,
		widthSize: 120,
		visibility: gridColumnVisibilityEnums[1],
	},
	{
		columnId: "customerName",
		isVisible: true,
		ordinalPosition: 2,
		widthSize: 235,
		visibility: gridColumnVisibilityEnums[1],
	},
	{
		columnId: "shipmentDate",
		isVisible: false,
		ordinalPosition: 3,
		widthSize: 130,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "poNumber",
		isVisible: true,
		ordinalPosition: 4,
		widthSize: 100,
		visibility: gridColumnVisibilityEnums[1],
	},
	{
		columnId: "originAddress",
		isVisible: true,
		ordinalPosition: 5,
		widthSize: 175,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "destinationAddress",
		isVisible: true,
		ordinalPosition: 6,
		widthSize: 175,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "totalWeight",
		isVisible: true,
		ordinalPosition: 7,
		widthSize: 85,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "statusDisplay",
		isVisible: true,
		ordinalPosition: 8,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "customerRate",
		isVisible: true,
		ordinalPosition: 9,
		widthSize: 100,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "carrierName",
		isVisible: false,
		ordinalPosition: 10,
		widthSize: 200,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "carrierRate",
		isVisible: false,
		ordinalPosition: 11,
		widthSize: 120,
		visibility: gridColumnVisibilityEnums[1],
	},
	{
		columnId: "maxLiability",
		isVisible: false,
		ordinalPosition: 12,
		widthSize: 260,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "margin",
		isVisible: false,
		ordinalPosition: 13,
		widthSize: 175,
		visibility: gridColumnVisibilityEnums[1],
	},
	{
		columnId: "createdByName",
		isVisible: true,
		ordinalPosition: 14,
		widthSize: 140,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "ltlOwner",
		isVisible: true,
		ordinalPosition: 15,
		widthSize: 140,
		visibility: gridColumnVisibilityEnums[1],
	},
	{
		columnId: "ftlOwner",
		isVisible: false,
		ordinalPosition: 16,
		widthSize: 175,
		visibility: gridColumnVisibilityEnums[1],
	},
	{
		columnId: "createdDate",
		isVisible: false,
		ordinalPosition: 17,
		widthSize: 130,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "customerReferences",
		isVisible: false,
		ordinalPosition: 18,
		widthSize: 180,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "transitDays",
		isVisible: false,
		ordinalPosition: 19,
		widthSize: 130,
		visibility: gridColumnVisibilityEnums[3],
	},
];

const BULK_QUOTE_BOARD_VISIBLE_COLUMNS = [
	"quoteId",
	"customerName",
	"originAddress",
	"destinationAddress",
	"statusDisplay",
	"carrierName",
	"transitDays",
	"customerRate",
	"maxLiability",
] as const;

const bulkQuoteBoardVisibleColumnOrder = new Map<string, number>(
	BULK_QUOTE_BOARD_VISIBLE_COLUMNS.map(
		(columnId, index) => [columnId, index] as const,
	),
);

const bulkQuoteBoardDefaultColumns = quoteBoardDefaultColumns
	.map((column) => {
		const visiblePosition = bulkQuoteBoardVisibleColumnOrder.get(
			column.columnId,
		);

		return {
			...column,
			isVisible: visiblePosition !== undefined,
			ordinalPosition:
				visiblePosition ??
				BULK_QUOTE_BOARD_VISIBLE_COLUMNS.length + column.ordinalPosition,
		};
	})
	.sort((first, second) => first.ordinalPosition - second.ordinalPosition)
	.map((column, ordinalPosition) => ({ ...column, ordinalPosition }));

export const defaultQuoteBoardGridPrefs: GridPrefsResponse = {
	[GridIdentifiers[QUOTE_BOARD]]: {
		gridId: QUOTE_BOARD,
		columns: quoteBoardDefaultColumns,
	},
};

export const defaultBulkQuoteBoardGridPrefs: GridPrefsResponse = {
	[GridIdentifiers[QUOTE_BOARD]]: {
		gridId: QUOTE_BOARD,
		columns: bulkQuoteBoardDefaultColumns,
	},
};

/**
 * Returns Quote Board defaults for the selected quote type. Deployment-specific
 * visibility is resolved downstream by `applyGridPrefs`.
 */
export const getDefaultQuoteBoardGridPrefs = (
	isInternal: boolean,
	quoteType = "Individual",
): GridPrefsResponse =>
	quoteType === "Bulk"
		? defaultBulkQuoteBoardGridPrefs
		: defaultQuoteBoardGridPrefs;

const TEMPLATES: GridIdentifierName = "Templates";

/**
 * Default Templates grid preferences. Derived from `useTemplateColumns`.
 *
 * The Templates page is available to both deployment types and every data
 * column is visible to both, so each entry carries `All` visibility. The
 * trailing action columns (the `isDefault` toggle and the `action` buttons)
 * are intentionally omitted — they are pinned to the end of the grid outside
 * the preference system (see `useTemplateGridColumns`).
 */
const templatesDefaultColumns: GridPrefColumn[] = [
	{
		columnId: "templateName",
		isVisible: true,
		ordinalPosition: 0,
		widthSize: 140,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "shipperName",
		isVisible: true,
		ordinalPosition: 1,
		widthSize: 160,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "pickupLocation",
		isVisible: true,
		ordinalPosition: 2,
		widthSize: 155,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "deliveryName",
		isVisible: true,
		ordinalPosition: 3,
		widthSize: 175,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "deliveryLocation",
		isVisible: true,
		ordinalPosition: 4,
		widthSize: 170,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "commodity",
		isVisible: true,
		ordinalPosition: 5,
		widthSize: 145,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "nmfc",
		isVisible: true,
		ordinalPosition: 6,
		widthSize: 80,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "freightClass",
		isVisible: true,
		ordinalPosition: 7,
		widthSize: 65,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "quantity",
		isVisible: true,
		ordinalPosition: 8,
		widthSize: 95,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "account",
		isVisible: true,
		ordinalPosition: 9,
		widthSize: 100,
		visibility: gridColumnVisibilityEnums[3],
	},
];

export const defaultTemplatesGridPrefs: GridPrefsResponse = {
	[GridIdentifiers[TEMPLATES]]: {
		gridId: TEMPLATES,
		columns: templatesDefaultColumns,
	},
};

/**
 * Returns the Templates default preferences. The same canonical list is
 * returned for both deployment types; the `isInternal` parameter is accepted
 * to mirror the other `getDefault*GridPrefs` helpers for consistency.
 */
export const getDefaultTemplatesGridPrefs = (
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Signature mirrors `getDefaultShipmentBoardGridPrefs` for consistency across grids.
	isInternal: boolean,
): GridPrefsResponse => defaultTemplatesGridPrefs;

const ADDRESS_BOOK: GridIdentifierName = "Address Book";

/**
 * Base (external) Address Book grid preferences. Derived from the data columns
 * built in `useAddressBookColumns`.
 *
 * Only the reorderable data columns are listed here. The trailing `action`
 * column (favorite / edit / delete buttons) is pinned to the end of the grid
 * outside the preference system, and the internal-only `isLegacy` chip column
 * is added for the internal deployment by `addressBookInternalDefaultColumns`.
 */
const addressBookDefaultColumns: GridPrefColumn[] = [
	{
		columnId: "companyName",
		isVisible: true,
		ordinalPosition: 0,
		widthSize: 200,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "fullAddress",
		isVisible: true,
		ordinalPosition: 1,
		widthSize: 400,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "address1",
		isVisible: false,
		ordinalPosition: 2,
		widthSize: 200,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "address2",
		isVisible: false,
		ordinalPosition: 3,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "city",
		isVisible: false,
		ordinalPosition: 4,
		widthSize: 120,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "stateProvince",
		isVisible: false,
		ordinalPosition: 5,
		widthSize: 100,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "postalCode",
		isVisible: false,
		ordinalPosition: 6,
		widthSize: 100,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "country",
		isVisible: false,
		ordinalPosition: 7,
		widthSize: 100,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "createdDate",
		isVisible: false,
		ordinalPosition: 8,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "isValidated",
		isVisible: false,
		ordinalPosition: 9,
		widthSize: 100,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "pickupContactName",
		isVisible: true,
		ordinalPosition: 10,
		widthSize: 180,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "deliveryContactName",
		isVisible: true,
		ordinalPosition: 11,
		widthSize: 180,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "default",
		isVisible: true,
		ordinalPosition: 12,
		widthSize: 160,
		visibility: gridColumnVisibilityEnums[3],
	},
];

export const defaultAddressBookGridPrefs: GridPrefsResponse = {
	[GridIdentifiers[ADDRESS_BOOK]]: {
		gridId: ADDRESS_BOOK,
		columns: addressBookDefaultColumns,
	},
};

/** Width (px) of the leading internal-only legacy chip column (see `useAddressBookColumns`). */
const ADDRESS_BOOK_LEGACY_COLUMN_WIDTH = 85;

/**
 * Internal Address Book defaults: the leading internal-only `isLegacy` chip
 * column pinned at ordinal position 0, followed by the canonical data columns
 * (shifted down by one). Defining the chip here — rather than special-casing
 * the grid-column hook — keeps the column builder and the sync's `defaultPrefs`
 * in agreement, so the chip stays leading on first load/reset and is seeded
 * from real defaults instead of synthetic placeholders on first interaction.
 */
const addressBookInternalDefaultColumns: GridPrefColumn[] = [
	{
		columnId: "isLegacy",
		isVisible: true,
		ordinalPosition: 0,
		widthSize: ADDRESS_BOOK_LEGACY_COLUMN_WIDTH,
		visibility: gridColumnVisibilityEnums[1],
	},
	...addressBookDefaultColumns.map((column) => ({
		...column,
		ordinalPosition: column.ordinalPosition + 1,
	})),
];

export const defaultAddressBookGridPrefsInternal: GridPrefsResponse = {
	[GridIdentifiers[ADDRESS_BOOK]]: {
		gridId: ADDRESS_BOOK,
		columns: addressBookInternalDefaultColumns,
	},
};

/**
 * Returns the Address Book default preferences for the given deployment. The
 * internal deployment includes the leading `isLegacy` chip column; the external
 * deployment does not. Deployment-specific column visibility is still resolved
 * downstream by `applyGridPrefs` using each column's `visibility`.
 */
export const getDefaultAddressBookGridPrefs = (
	isInternal: boolean,
): GridPrefsResponse =>
	isInternal
		? defaultAddressBookGridPrefsInternal
		: defaultAddressBookGridPrefs;

const SHIPMENT_DETAILS_REPORT: GridIdentifierName = "Shipment Details Report";

/**
 * Default Shipment Details Report grid preferences. Derived from
 * `SHIPMENT_DETAIL_COLUMNS`
 * (`src/features/reports/constants/shipment-detail-columns.tsx`) — `columnId`
 * is the column `field`, `isVisible` the default `show`, `ordinalPosition` the
 * source array order, and `widthSize` the column's default (readable) width.
 *
 * Kept as a hand-maintained literal (like `quoteBoardDefaultColumns`) rather
 * than imported from the feature `constants` to avoid a shared -> feature
 * dependency; `default-grid-prefs.test.ts` guards against drift by asserting
 * these `columnId`s match `SHIPMENT_DETAIL_COLUMNS` field order.
 */
const shipmentDetailReportDefaultColumns: GridPrefColumn[] = [
	{
		columnId: "rateType",
		isVisible: true,
		ordinalPosition: 0,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "customerPO",
		isVisible: true,
		ordinalPosition: 1,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "poNumber",
		isVisible: true,
		ordinalPosition: 2,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "deliveryApptDateTime",
		isVisible: true,
		ordinalPosition: 3,
		widthSize: 200,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "scheduledDeliveryTime",
		isVisible: true,
		ordinalPosition: 4,
		widthSize: 200,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "originCity",
		isVisible: true,
		ordinalPosition: 5,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "originStateProvinceCode",
		isVisible: true,
		ordinalPosition: 6,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "destinationCity",
		isVisible: true,
		ordinalPosition: 7,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "destinationStateProvinceCode",
		isVisible: true,
		ordinalPosition: 8,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "loadStatus",
		isVisible: true,
		ordinalPosition: 9,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "pickupApptDateTime",
		isVisible: false,
		ordinalPosition: 10,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "scheduledPickupTime",
		isVisible: false,
		ordinalPosition: 11,
		widthSize: 200,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "actualPickupDateTime",
		isVisible: false,
		ordinalPosition: 12,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "actualDeliveryDateTime",
		isVisible: false,
		ordinalPosition: 13,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "originName",
		isVisible: false,
		ordinalPosition: 14,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "originPostalCode",
		isVisible: false,
		ordinalPosition: 15,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "originCountryCode",
		isVisible: false,
		ordinalPosition: 16,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "destinationName",
		isVisible: false,
		ordinalPosition: 17,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "destinationPostalCode",
		isVisible: false,
		ordinalPosition: 18,
		widthSize: 200,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "destinationCountryCode",
		isVisible: false,
		ordinalPosition: 19,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "trailerType",
		isVisible: false,
		ordinalPosition: 20,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "trailerSize",
		isVisible: false,
		ordinalPosition: 21,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "loadWeight",
		isVisible: false,
		ordinalPosition: 22,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "palletCount",
		isVisible: false,
		ordinalPosition: 23,
		widthSize: 210,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "handlingUnits",
		isVisible: false,
		ordinalPosition: 24,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "class",
		isVisible: false,
		ordinalPosition: 25,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "practicalMiles",
		isVisible: false,
		ordinalPosition: 26,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "totalBillAmount",
		isVisible: false,
		ordinalPosition: 27,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "numberOfStops",
		isVisible: false,
		ordinalPosition: 28,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "lane",
		isVisible: false,
		ordinalPosition: 29,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "proNumber",
		isVisible: false,
		ordinalPosition: 30,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "soNumber",
		isVisible: false,
		ordinalPosition: 31,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "containerNumber",
		isVisible: false,
		ordinalPosition: 32,
		widthSize: 150,
		visibility: gridColumnVisibilityEnums[3],
	},
];

export const defaultShipmentDetailReportGridPrefs: GridPrefsResponse = {
	[GridIdentifiers[SHIPMENT_DETAILS_REPORT]]: {
		gridId: SHIPMENT_DETAILS_REPORT,
		columns: shipmentDetailReportDefaultColumns,
	},
};

/**
 * Returns the Shipment Details Report default preferences. The same canonical
 * list is returned for both deployment types (the report is external-facing);
 * the `isInternal` parameter exists only for signature parity with the other
 * `getDefault*GridPrefs` helpers.
 */
export const getDefaultShipmentDetailReportGridPrefs = (
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Signature mirrors `getDefaultShipmentBoardGridPrefs` for consistency across grids.
	isInternal: boolean,
): GridPrefsResponse => defaultShipmentDetailReportGridPrefs;

const COMMODITIES: GridIdentifierName = "Commodities";

const commoditiesDefaultColumns: GridPrefColumn[] = [
	{
		columnId: "description",
		isVisible: true,
		ordinalPosition: 0,
		widthSize: 285,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "quantity",
		isVisible: true,
		ordinalPosition: 1,
		widthSize: 75,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "freightClass",
		isVisible: true,
		ordinalPosition: 2,
		widthSize: 75,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "unitType",
		isVisible: true,
		ordinalPosition: 3,
		widthSize: 75,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "weight",
		isVisible: true,
		ordinalPosition: 4,
		widthSize: 100,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "dimensions",
		isVisible: true,
		ordinalPosition: 5,
		widthSize: 125,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "nmfc",
		isVisible: true,
		ordinalPosition: 6,
		widthSize: 95,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "isStackable",
		isVisible: true,
		ordinalPosition: 7,
		widthSize: 75,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "isHazmat",
		isVisible: true,
		ordinalPosition: 8,
		widthSize: 75,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "isDefault",
		isVisible: true,
		ordinalPosition: 9,
		widthSize: 75,
		visibility: gridColumnVisibilityEnums[3],
	},
];

export const defaultCommoditiesGridPrefs: GridPrefsResponse = {
	[GridIdentifiers[COMMODITIES]]: {
		gridId: COMMODITIES,
		columns: commoditiesDefaultColumns,
	},
};

/**
 * Returns the Commodities default preferences.
 */
export const getDefaultCommoditiesGridPrefs = (
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Signature mirrors other grid default helpers for consistency.
	isInternal: boolean,
): GridPrefsResponse => defaultCommoditiesGridPrefs;

const PAYMENT: GridIdentifierName = "Payment";

/**
 * Default Payment (Invoices) board grid preferences. Derived from
 * `getPaymentColumns`.
 *
 * A single canonical column list is used for both deployment types — the
 * Invoices board is an external-only persistence surface (internal users see
 * the UI but their layout is never stored), and every data column is available
 * to both audiences, so each entry carries `All` visibility. The pinned select
 * (`""`) and `docs` action columns are intentionally omitted — they are pinned
 * by `applyGridPrefs` (empty `field` / `required: true`) and carry no
 * `GridPrefColumn`.
 */
const paymentDefaultColumns: GridPrefColumn[] = [
	{
		columnId: "invoiceStatus",
		isVisible: true,
		ordinalPosition: 0,
		widthSize: 110,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "poNumber",
		isVisible: true,
		ordinalPosition: 1,
		widthSize: 120,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "custPO",
		isVisible: true,
		ordinalPosition: 2,
		widthSize: 140,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "balanceAmt",
		isVisible: true,
		ordinalPosition: 3,
		widthSize: 110,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "paidAmt",
		isVisible: true,
		ordinalPosition: 4,
		widthSize: 110,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "invoiceAmt",
		isVisible: true,
		ordinalPosition: 5,
		widthSize: 110,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "dueDate",
		isVisible: true,
		ordinalPosition: 6,
		widthSize: 120,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "docdate",
		isVisible: true,
		ordinalPosition: 7,
		widthSize: 120,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "age",
		isVisible: true,
		ordinalPosition: 8,
		widthSize: 100,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "deliveryDate",
		isVisible: true,
		ordinalPosition: 9,
		widthSize: 120,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "mode",
		isVisible: false,
		ordinalPosition: 10,
		widthSize: 100,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "firstPickupState",
		isVisible: false,
		ordinalPosition: 11,
		widthSize: 100,
		visibility: gridColumnVisibilityEnums[3],
	},
	{
		columnId: "lastDropState",
		isVisible: false,
		ordinalPosition: 12,
		widthSize: 100,
		visibility: gridColumnVisibilityEnums[3],
	},
];

export const defaultPaymentGridPrefs: GridPrefsResponse = {
	[GridIdentifiers[PAYMENT]]: {
		gridId: PAYMENT,
		columns: paymentDefaultColumns,
	},
};

/**
 * Returns the Payment board default preferences. The same canonical list is
 * returned for both deployment types; the `isInternal` parameter is accepted
 * to mirror the other `getDefault*GridPrefs` helpers for consistency.
 */
export const getDefaultPaymentGridPrefs = (
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Signature mirrors `getDefaultShipmentBoardGridPrefs` for consistency across grids.
	isInternal: boolean,
): GridPrefsResponse => defaultPaymentGridPrefs;
